/* THE COMMAND PALETTE's vocabulary and ranking — pure, executed by `commands.test.ts`.
 *
 * ⌘K / Ctrl+K opens it anywhere signed in (docs/WEB-DESIGN-DIRECTION.md §2, "the works amazingly
 * half"). A command is a place to GO or a thing to MAKE; the list is the routes the app already
 * has plus the coach's own clients and workouts by name. Ranking is deliberately simple and
 * readable: every query word must appear as a subsequence, a prefix match outranks a middle one,
 * and ties keep the order they were given (clients before workouts before places).
 */
import { href, type Route } from "./route";
import { COACH_PAGE_TITLE } from "./communityFeature";

export type CommandKind = "go" | "client" | "workout" | "exercise" | "new";

export type Command = {
  id: string;
  kind: CommandKind;
  title: string;
  /** One quiet word to the right: "Client", "Workout", "Go", "New". */
  hint: string;
  route: Route;
};

export function placeCommands(): Command[] {
  return [
    { id: "go:roster", kind: "go", title: "Clients", hint: "Go", route: { name: "roster" } },
    { id: "go:workouts", kind: "go", title: "Workouts", hint: "Go", route: { name: "workouts" } },
    { id: "go:inbox", kind: "go", title: "Inbox", hint: "Go", route: { name: "inbox" } },
    // JOB 3 STEP 5 PAGE 6: the place is called what the page calls itself (`Settings` with the Community out — it said
    // `Your coaching page`, a page that is not drawn).
    { id: "go:page", kind: "go", title: COACH_PAGE_TITLE, hint: "Go", route: { name: "page" } },
    { id: "new:workout", kind: "new", title: "New workout", hint: "New", route: { name: "workoutNew" } },
  ];
}

export function clientCommand(clientId: string, name: string): Command {
  return { id: `client:${clientId}`, kind: "client", title: name, hint: "Client", route: { name: "client", clientId } };
}

export function workoutCommand(workoutId: string, name: string): Command {
  return { id: `workout:${workoutId}`, kind: "workout", title: name, hint: "Workout", route: { name: "workout", workoutId } };
}

/* The shelf's rows are commands too. A coach edits an exercise far more often than they create
   one, and the shelf is two clicks away from every screen — so the palette is the short road to
   it. The list is drawn from the shelf's OWN query, never a second one: one vocabulary. */
export function exerciseCommand(exerciseId: string, name: string): Command {
  return { id: `exercise:${exerciseId}`, kind: "exercise", title: name, hint: "Exercise", route: { name: "exercise", exerciseId } };
}

/** 0 = no match; higher is better. Prefix 3, word-start 2, subsequence 1 — per query word, summed. */
export function score(query: string, title: string): number {
  const words = query.trim().toLowerCase().split(/\s+/).filter((w) => w !== "");
  if (words.length === 0) return 1;
  const t = title.toLowerCase();
  let total = 0;
  for (const w of words) {
    if (t.startsWith(w)) total += 3;
    else if (t.split(/[\s·(/-]+/).some((part) => part.startsWith(w))) total += 2;
    else if (isSubsequence(w, t)) total += 1;
    else return 0;
  }
  return total;
}

function isSubsequence(needle: string, hay: string): boolean {
  let i = 0;
  for (const ch of hay) if (ch === needle[i]) i += 1;
  return i === needle.length;
}

export function rank(query: string, commands: Command[], limit = 8): Command[] {
  return commands
    .map((c, i) => ({ c, s: score(query, c.title), i }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.c);
}

export function go(command: Command): void {
  window.location.hash = href(command.route);
}
